import { AttendanceClockAction, localToday } from './attendanceClock';

export const ATTENDANCE_CLOCK_UPDATED_EVENT = 'teamlio:attendance-clock-updated';

export interface AttendanceClockUpdatedDetail {
  action?: AttendanceClockAction;
  clockedIn?: boolean;
  source?: string;
  userId?: string;
  sourceId?: string;
  occurredAt?: string;
}

const OPTIMISTIC_ATTENDANCE_SOURCES = new Set([
  'self_clock_in',
  'self_clock_out',
  'manager_working_hours',
]);

export const isOptimisticAttendanceClockUpdate = (
  detail: AttendanceClockUpdatedDetail,
): detail is AttendanceClockUpdatedDetail & { clockedIn: boolean } =>
  typeof detail.clockedIn === 'boolean' &&
  (detail.source === undefined || OPTIMISTIC_ATTENDANCE_SOURCES.has(detail.source));

// WebSocket events can arrive after a reconnect. An old clock-in must refresh
// the page, but it must never override today's REST-backed clock state.
export const isAttendanceClockEventForDate = (
  detail: AttendanceClockUpdatedDetail,
  date: string,
): boolean => {
  if (!detail.occurredAt) return true;

  const occurredAt = new Date(detail.occurredAt);
  return !Number.isNaN(occurredAt.getTime()) && localToday(occurredAt) === date;
};

export const emitAttendanceClockUpdated = (
  detail: AttendanceClockUpdatedDetail = {},
): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<AttendanceClockUpdatedDetail>(ATTENDANCE_CLOCK_UPDATED_EVENT, {
      detail,
    }),
  );
};

export const subscribeAttendanceClockUpdated = (
  listener: (detail: AttendanceClockUpdatedDetail) => void,
): (() => void) => {
  if (typeof window === 'undefined') return () => {};

  const handler = (event: Event) => {
    listener((event as CustomEvent<AttendanceClockUpdatedDetail>).detail || {});
  };

  window.addEventListener(ATTENDANCE_CLOCK_UPDATED_EVENT, handler);
  return () => window.removeEventListener(ATTENDANCE_CLOCK_UPDATED_EVENT, handler);
};
